import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { FileText, Users, MapPin, CalendarClock, CalendarCheck, CalendarX, Clock, Send, Sparkles, Receipt, Building2 } from 'lucide-react';
import { getOpleiderRfqs, verstuurOfferte, legUitvoerdatumVast } from '../services/b2bAcademyApi';
import type { Rfq, RfqStatus } from '../services/b2bAcademyApi';
import { LOCATIE_OPTIES, PLANNING_OPTIES, eur, datumKort } from '../components/b2bAcademy/b2bStijl';
import { berekenMatrix } from '../utils/academyMatrix';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}


const STATUS_CLS: Record<RfqStatus, string> = {
  RFQ_SUBMITTED: 'bg-blue-100 text-blue-800',
  OFFER_SENT: 'bg-amber-100 text-amber-800',
  OFFER_ACCEPTED: 'bg-green-100 text-green-800',
  OFFER_DECLINED: 'bg-gray-100 text-gray-600',
};

/** Live post-pay preview van de splitsing terwijl de opleider de marktprijs typt (Core-Matrix, centen-exact). */
function splitsing(marktprijs: number) {
  const m = berekenMatrix(marktprijs);
  return { korting: m.korting_bedrag, offertewaarde: m.activeringswaarde, fee: m.platformfee_bedrag, netto: m.opleider_netto };
}

/**
 * Post-pay facturatie (migratie 156): de opleider legt de laatste trainingsdag vast; de werkgever
 * bevestigt of betwist die datum (besluit 5, migratie 197). Pas na bevestiging factureert Belagom
 * de werkgever op de 1e van de maand en betaalt dan 85% uit. Na facturering ligt de datum vast en
 * toont het blok de uitbetaalstatus.
 */
const UitvoerdatumBlok: React.FC<{ rfq: Rfq }> = ({ rfq }) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [datum, setDatum] = useState(rfq.uitvoerdatum ?? '');
  const mutation = useMutation({
    mutationFn: () => legUitvoerdatumVast(rfq.id, datum),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['opleider-rfqs'] }),
  });
  const leesbaar = rfq.uitvoerdatum ? datumKort(`${rfq.uitvoerdatum}T12:00:00`) : null;

  if (rfq.gefactureerd) {
    const uitbetaald = rfq.opleider_payout_status === 'completed';
    const mislukt = rfq.opleider_payout_status === 'failed';
    return (
      <p className={`inline-flex items-center gap-1.5 text-sm sm:col-span-4 ${mislukt ? 'text-red-700' : 'text-green-700'}`}>
        <Receipt className="h-4 w-4" aria-hidden="true" />
        {uitbetaald
          ? t('b2bAcademy.opleider.uitbetaald', { defaultValue: 'Gefactureerd via BeLagom en uitbetaald ({{bedrag}} excl. btw) · training {{datum}}.', bedrag: eur.format(rfq.netto_opleider ?? 0), datum: leesbaar ?? '' })
          : mislukt
          ? t('b2bAcademy.opleider.uitbetalingMislukt', { defaultValue: 'Gefactureerd · uitbetaling mislukt — controleer uw Stripe-koppeling onder Finance. BeLagom probeert het opnieuw.' })
          : t('b2bAcademy.opleider.gefactureerd', { defaultValue: 'Gefactureerd via de Academy-verzamelfactuur · training {{datum}} · uitbetaling {{bedrag}} (excl. btw) volgt.', bedrag: eur.format(rfq.netto_opleider ?? 0), datum: leesbaar ?? '' })}
      </p>
    );
  }

  return (
    <div className="space-y-2 sm:col-span-4">
      <p className="inline-flex items-center gap-1.5 text-sm text-green-700">
        <Receipt className="h-4 w-4" aria-hidden="true" />
        {t('b2bAcademy.opleider.factuuropdracht', { defaultValue: 'Factuuropdracht {{nummer}}: BeLagom factureert {{bedrag}} achteraf aan de werkgever — u factureert zelf niet. Plan de data in overleg en leg hieronder de laatste trainingsdag vast.', nummer: rfq.factuur_opdracht_nummer ?? '', bedrag: eur.format(rfq.offertewaarde ?? 0) })}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor={`uitvoer-${rfq.id}`}>{t('b2bAcademy.opleider.uitvoerdatum', { defaultValue: 'Uitvoerdatum (laatste trainingsdag)' })}</label>
          <input id={`uitvoer-${rfq.id}`} type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]" />
        </div>
        <button type="button" onClick={() => mutation.mutate()} disabled={!datum || (datum === rfq.uitvoerdatum && rfq.uitvoerdatum_status !== 'betwist') || mutation.isPending} className="inline-flex items-center gap-2 rounded-lg bg-[#0B1F3A] px-3 py-2 text-sm font-semibold text-white hover:bg-[#002266] disabled:cursor-not-allowed disabled:opacity-50">
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
          {rfq.uitvoerdatum ? t('b2bAcademy.opleider.uitvoerdatumWijzig', { defaultValue: 'Datum wijzigen' }) : t('b2bAcademy.opleider.uitvoerdatumOpslaan', { defaultValue: 'Datum vastleggen' })}
        </button>
      </div>
      {rfq.uitvoerdatum_status === 'wacht_op_werkgever' && (
        <p className="inline-flex items-center gap-1.5 text-sm text-amber-700">
          <Clock className="h-4 w-4" aria-hidden="true" />
          {t('b2bAcademy.opleider.wachtOpBevestiging', { defaultValue: 'Uitvoerdatum {{datum}} wacht op bevestiging van de werkgever.', datum: leesbaar ?? '' })}
        </p>
      )}
      {rfq.uitvoerdatum_status === 'bevestigd' && (
        <p className="inline-flex items-center gap-1.5 text-sm text-green-700">
          <CalendarCheck className="h-4 w-4" aria-hidden="true" />
          {t('b2bAcademy.opleider.bevestigd', { defaultValue: 'De werkgever heeft de uitvoerdatum {{datum}} bevestigd · komt op de verzamelfactuur van de 1e.', datum: leesbaar ?? '' })}
        </p>
      )}
      {rfq.uitvoerdatum_status === 'betwist' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="status">
          <p className="inline-flex items-center gap-1.5 font-semibold">
            <CalendarX className="h-4 w-4" aria-hidden="true" />
            {t('b2bAcademy.opleider.betwist', { defaultValue: 'De werkgever betwist de uitvoerdatum {{datum}}', datum: leesbaar ?? '' })}
          </p>
          {rfq.uitvoerdatum_betwist_reden && <p className="mt-1 whitespace-pre-line text-xs">“{rfq.uitvoerdatum_betwist_reden}”</p>}
          <p className="mt-1 text-xs">{t('b2bAcademy.opleider.betwistActie', { defaultValue: 'Stem de datum af met de werkgever en leg de juiste uitvoerdatum opnieuw vast. Tot de werkgever bevestigt, wordt er niet gefactureerd of uitbetaald.' })}</p>
        </div>
      )}
      <p className="text-xs text-gray-500">{t('b2bAcademy.opleider.uitvoerdatumUitlegBevestiging', { defaultValue: 'De werkgever bevestigt deze datum na afloop. Op de 1e van de maand daarna factureert BeLagom de werkgever en ontvangt u uw netto uitbetaling (85% + btw).' })}</p>
      {mutation.isError && <p className="text-sm text-red-600" role="alert">{foutMelding(mutation.error, t('b2bAcademy.opleider.uitvoerdatumFout', { defaultValue: 'De uitvoerdatum kon niet worden vastgelegd.' }))}</p>}
    </div>
  );
};

const RfqRegel: React.FC<{ rfq: Rfq }> = ({ rfq }) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [prijs, setPrijs] = useState(rfq.marktprijs ? String(rfq.marktprijs) : '');
  const [toelichting, setToelichting] = useState(rfq.offerte_toelichting ?? '');
  const bedrag = parseFloat(prijs.replace(',', '.'));
  const preview = Number.isFinite(bedrag) && bedrag > 0 ? splitsing(bedrag) : null;
  const mutation = useMutation({
    mutationFn: () => verstuurOfferte(rfq.id, { marktprijs: bedrag, toelichting: toelichting.trim() || null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['opleider-rfqs'] }),
  });
  const locatie = LOCATIE_OPTIES.find((o) => o.key === rfq.locatie_voorkeur);
  const planning = PLANNING_OPTIES.find((o) => o.key === rfq.planning);
  const statusLabel: Record<RfqStatus, string> = {
    RFQ_SUBMITTED: t('b2bAcademy.opleider.statusNieuw', { defaultValue: 'Nieuwe aanvraag — offerte gevraagd' }),
    OFFER_SENT: t('b2bAcademy.opleider.statusVerzonden', { defaultValue: 'Offerte verzonden — wacht op werkgever' }),
    OFFER_ACCEPTED: t('b2bAcademy.opleider.statusGeaccepteerd', { defaultValue: 'Geaccepteerd — plan data & leg uitvoerdatum vast' }),
    OFFER_DECLINED: t('b2bAcademy.opleider.statusAfgewezen', { defaultValue: 'Afgewezen (archief)' }),
  };
  const bewerkbaar = rfq.status === 'RFQ_SUBMITTED' || rfq.status === 'OFFER_SENT';

  return (
    <li className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-gray-900">{rfq.titel}</h3>
          <p className="text-sm text-gray-500">
            <Building2 className="mr-1 inline h-4 w-4 align-text-bottom" aria-hidden="true" />
            {rfq.bedrijfsnaam ?? t('b2bAcademy.opleider.onbekendBedrijf', { defaultValue: 'Opdrachtgever' })} · {datumKort(rfq.created_at)}
          </p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_CLS[rfq.status]}`}>{statusLabel[rfq.status]}</span>
      </div>

      <dl className="mt-3 grid gap-2 text-sm text-gray-700 sm:grid-cols-3">
        <div className="flex items-center gap-2"><Users className="h-4 w-4 text-gray-400" aria-hidden="true" /><span><strong>{rfq.deelnemers}</strong> {t('b2bAcademy.rfq.deelnemers', { defaultValue: 'deelnemers' })}{rfq.afdeling ? ` · ${rfq.afdeling}` : ''}</span></div>
        <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gray-400" aria-hidden="true" /><span>{locatie?.emoji} {locatie?.label ?? rfq.locatie_voorkeur}</span></div>
        <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-gray-400" aria-hidden="true" /><span>{planning?.emoji} {planning?.label ?? rfq.planning}</span></div>
      </dl>
      {rfq.notities && (
        <p className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
          <strong className="text-gray-900">{t('b2bAcademy.opleider.casuistiek', { defaultValue: 'Casuïstiek & maatwerk:' })}</strong> <span className="whitespace-pre-line">{rfq.notities}</span>
        </p>
      )}

      {bewerkbaar ? (
        <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_20rem]">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={`prijs-${rfq.id}`}>{t('b2bAcademy.opleider.marktprijs', { defaultValue: 'Bruto marktprijs (excl. btw)' })}</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                <input id={`prijs-${rfq.id}`} type="number" min={1} step="0.01" value={prijs} onChange={(e) => setPrijs(e.target.value)} className="w-full rounded-lg border border-gray-300 py-2.5 pl-8 pr-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]" placeholder="4000" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={`toel-${rfq.id}`}>{t('b2bAcademy.opleider.toelichting', { defaultValue: 'Toelichting op de offerte (optioneel)' })}</label>
              <textarea id={`toel-${rfq.id}`} rows={3} maxLength={4000} value={toelichting} onChange={(e) => setToelichting(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]" placeholder={t('b2bAcademy.opleider.toelichtingPh', { defaultValue: 'bijv. Inclusief intake, 2 trainers en digitaal naslagwerk; reiskosten inbegrepen.' })} />
            </div>
            {mutation.isError && <p className="text-sm text-red-600" role="alert">{foutMelding(mutation.error, t('b2bAcademy.opleider.fout', { defaultValue: 'De offerte kon niet worden verzonden.' }))}</p>}
            {mutation.isSuccess && <p className="text-sm text-green-700" role="status">{t('b2bAcademy.opleider.verzonden', { defaultValue: 'Offerte verzonden. De werkgever is geïnformeerd.' })}</p>}
            <button type="button" onClick={() => mutation.mutate()} disabled={!preview || mutation.isPending} className="inline-flex items-center gap-2 rounded-lg bg-[#0B1F3A] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#002266] disabled:cursor-not-allowed disabled:opacity-50">
              <Send className="h-4 w-4" aria-hidden="true" />
              {rfq.status === 'OFFER_SENT' ? t('b2bAcademy.opleider.opnieuw', { defaultValue: 'Offerte bijwerken' }) : t('b2bAcademy.opleider.verstuur', { defaultValue: 'Offerte versturen' })}
            </button>
          </div>
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm">
            <p className="mb-2 font-semibold text-[#0B1F3A]">{t('b2bAcademy.opleider.splitsing', { defaultValue: 'Post-pay splitsing (automatisch)' })}</p>
            <dl className="space-y-1.5">
              <div className="flex justify-between"><dt className="text-gray-600">{t('b2bAcademy.opleider.ingevoerd', { defaultValue: 'Ingevoerde marktprijs' })}</dt><dd className="font-medium text-gray-900">{preview ? eur.format(bedrag) : '—'}</dd></div>
              <div className="flex justify-between"><dt className="inline-flex items-center gap-1 text-emerald-700"><Sparkles className="h-3.5 w-3.5" aria-hidden="true" />{t('b2bAcademy.opleider.korting', { defaultValue: 'BeLagom korting (5%)' })}</dt><dd className="text-emerald-700">{preview ? `− ${eur.format(preview.korting)}` : '—'}</dd></div>
              <div className="flex justify-between border-t border-blue-200 pt-1.5"><dt className="font-medium text-gray-800">{t('b2bAcademy.opleider.offertewaarde', { defaultValue: 'Offertewaarde (factuur werkgever)' })}</dt><dd className="font-bold text-gray-900">{preview ? eur.format(preview.offertewaarde) : '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-600">{t('b2bAcademy.opleider.fee', { defaultValue: 'BeLagom platformfee (10% over marktprijs)' })}</dt><dd className="text-gray-700">{preview ? `− ${eur.format(preview.fee)}` : '—'}</dd></div>
              <div className="flex justify-between border-t border-blue-200 pt-1.5"><dt className="font-semibold text-[#0B1F3A]">{t('b2bAcademy.opleider.netto', { defaultValue: 'Netto uitbetaling (85%)' })}</dt><dd className="font-bold text-[#0B1F3A]">{preview ? eur.format(preview.netto) : '—'}</dd></div>
            </dl>
          </div>
        </div>
      ) : (
        rfq.marktprijs !== null && (
          <div className="mt-4 grid gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm sm:grid-cols-4">
            <div><p className="text-xs text-gray-500">{t('b2bAcademy.opleider.ingevoerd', { defaultValue: 'Ingevoerde marktprijs' })}</p><p className="font-medium text-gray-900">{eur.format(rfq.marktprijs)}</p></div>
            <div><p className="text-xs text-gray-500">{t('b2bAcademy.opleider.offertewaarde', { defaultValue: 'Offertewaarde (factuur werkgever)' })}</p><p className="font-bold text-gray-900">{eur.format(rfq.offertewaarde ?? 0)}</p></div>
            <div><p className="text-xs text-gray-500">{t('b2bAcademy.opleider.fee', { defaultValue: 'BeLagom platformfee (10% over marktprijs)' })}</p><p className="text-gray-700">{eur.format(rfq.platformfee ?? 0)}</p></div>
            <div><p className="text-xs text-gray-500">{t('b2bAcademy.opleider.netto', { defaultValue: 'Netto uitbetaling (85%)' })}</p><p className="font-bold text-[#0B1F3A]">{eur.format(rfq.netto_opleider ?? 0)}</p></div>
            {rfq.status === 'OFFER_ACCEPTED' && <UitvoerdatumBlok rfq={rfq} />}
          </div>
        )
      )}
    </li>
  );
};

/** Opleider: incompany offerte-aanvragen (SIGNAL_B2B_INQUIRY) met marktprijs-invoer en post-pay splitsing. */
export const OpleiderOfferteAanvragenPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const query = useQuery({ queryKey: ['opleider-rfqs'], queryFn: getOpleiderRfqs, refetchInterval: 60_000 });
  const [filter, setFilter] = useState<'open' | 'alle'>('open');
  const rfqs = useMemo(() => {
    const alle = query.data ?? [];
    return filter === 'open' ? alle.filter((r) => r.status === 'RFQ_SUBMITTED' || r.status === 'OFFER_SENT') : alle;
  }, [query.data, filter]);
  const nieuw = (query.data ?? []).filter((r) => r.status === 'RFQ_SUBMITTED').length;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <FileText className="h-6 w-6 text-[#0B1F3A]" aria-hidden="true" />
            {t('b2bAcademy.opleider.titel', { defaultValue: 'Incompany offerte-aanvragen' })}
            {nieuw > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">{nieuw}</span>}
          </h1>
          <p className="mt-1 text-sm text-gray-600">{t('b2bAcademy.opleider.intro', { defaultValue: 'Opdrachtgevers vragen via de B2B Academy een offerte aan. Voer de bruto marktprijs in; BeLagom berekent de korting, de platformfee en uw netto uitbetaling.' })}</p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
          {(['open', 'alle'] as const).map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)} aria-pressed={filter === f} className={`rounded-md px-3 py-1.5 font-medium ${filter === f ? 'bg-[#0B1F3A] text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
              {f === 'open' ? t('b2bAcademy.opleider.filterOpen', { defaultValue: 'Openstaand' }) : t('b2bAcademy.opleider.filterAlle', { defaultValue: 'Alle' })}
            </button>
          ))}
        </div>
      </div>
      {query.isLoading && <p className="text-sm text-gray-500">{t('b2bAcademy.opleider.laden', { defaultValue: 'Laden…' })}</p>}
      {query.isError && <p className="text-sm text-red-600" role="alert">{foutMelding(query.error, t('b2bAcademy.opleider.laadfout', { defaultValue: 'Offerte-aanvragen konden niet worden geladen.' }))}</p>}
      {query.isSuccess && rfqs.length === 0 && (
        <p className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">{t('b2bAcademy.opleider.geen', { defaultValue: 'Geen offerte-aanvragen in deze weergave.' })}</p>
      )}
      <ul className="space-y-4">
        {rfqs.map((r) => <RfqRegel key={r.id} rfq={r} />)}
      </ul>
    </div>
  );
};

export default OpleiderOfferteAanvragenPage;
